import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { usePermissions, PERMISSION_STATE } from '../../context/PermissionContext';
import PermissionDenied from '../Common/PermissionDenied';

const LoadingSpinner = () => (
    <Box 
        sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            minHeight: '300px' 
        }}
    >
        <CircularProgress size={36} sx={{ color: '#FFBD65' }} />
    </Box>
);

const RouteGuard = ({ children }) => {
    const location = useLocation();
    const { getRoutePermission, resolvePermissionForRoute } = usePermissions();
    const currentPath = location.pathname;

    const routePerm = getRoutePermission(currentPath);

    useEffect(() => {
        if (routePerm.status === PERMISSION_STATE.UNKNOWN) {
            resolvePermissionForRoute(currentPath);
        }
    }, [currentPath, routePerm.status, resolvePermissionForRoute]);

    // 1. If status is UNKNOWN or LOADING, hold rendering of protected child component
    if (routePerm.status === PERMISSION_STATE.UNKNOWN || routePerm.status === PERMISSION_STATE.LOADING) {
        return <LoadingSpinner />;
    }

    // 2. If access is DENIED, display backend permission-denied message without mounting children
    if (routePerm.status === PERMISSION_STATE.DENIED) {
        return (
            <PermissionDenied 
                message={routePerm.deniedMessage || "Access denied: You do not have permission to view in menus"} 
                statusBadge={routePerm.statusBadge || "403 Forbidden"} 
            />
        );
    }

    // 3. If access is ALLOWED, mount protected children and allow its page APIs to execute
    if (routePerm.status === PERMISSION_STATE.ALLOWED) {
        return children;
    }

    // Safety fallback
    return <LoadingSpinner />;
};

export default RouteGuard;
